// =============================================================================
// TemplateForm.js  (Phase 4 – generischer Renderer)
// -----------------------------------------------------------------------------
// Rendert ein Formular datengetrieben anhand einer aufgeloesten System-Vorlage
// (siehe Backend-Resolve-API). Die Vorlage bestimmt, welche Felder sichtbar
// sind, deren Reihenfolge, Label, Pflichtstatus und Gruppierung – je Stufe.
//
// Die Persistenz bleibt beim Aufrufer (onSubmit erhaelt das Wertobjekt). So
// bleiben die bestehenden, validierten Entitaets-Endpunkte unveraendert.
//
// Props:
//   templateKey   (string)  technischer Vorlagentyp, z. B. "ENTITY_MASTER_DATA"
//   tier          (string)  Stufencode (ADMIN/MAINVO/SUBVO/ADM/CUSTOMER/EMPLOYEE)
//   initialValues (object)  Startwerte je field_key
//   onSubmit      (fn)      erhaelt das bereinigte Wertobjekt
//   onCancel      (fn)      optionaler Abbrechen-Handler
//   submitLabel   (string)  Beschriftung des Speichern-Buttons
//   columns       (1|2)     Spaltenlayout (Default 2, responsiv)
//   readOnly      (bool)    nur Anzeige
//   busy          (bool)    externer Lade-/Speicherzustand
//   hideActions   (bool)    Buttons ausblenden (z. B. fuer eingebettete Nutzung)
// =============================================================================

import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { Form, Row, Col, Button, Alert, Spinner } from "react-bootstrap";
import UserService from "../services/user.service";

// Mensch-lesbare Gruppen-Ueberschriften (konfigurierbar erweiterbar).
const GROUP_LABELS = {
  company: "Firma",
  contact: "Ansprechpartner",
  address: "Adresse",
  bank: "Bankverbindung",
  legal: "Rechtliches",
  general: "Allgemein",
  security: "Sicherheit",
};

const TemplateForm = forwardRef(function TemplateForm(
  {
    templateKey,
    tier,
    initialValues = {},
    onSubmit,
    onCancel,
    submitLabel = "Speichern",
    columns = 2,
    readOnly = false,
    busy = false,
    hideActions = false,
  },
  ref
) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Setzt Startwerte fuer eine Feldliste (Default-Werte beachten).
  const seedValues = useCallback(
    (fieldList) => {
      const next = {};
      fieldList.forEach((f) => {
        const fromInit = initialValues ? initialValues[f.field_key] : undefined;
        if (fromInit !== undefined && fromInit !== null) {
          next[f.field_key] = fromInit;
        } else if (f.field_type === "checkbox") {
          next[f.field_key] = f.default_value === "true" || f.default_value === true;
        } else {
          next[f.field_key] = f.default_value != null ? f.default_value : "";
        }
      });
      return next;
    },
    [initialValues]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    UserService.resolveFormTemplate(templateKey, tier)
      .then((res) => {
        if (!active) return;
        const list = (res.data && res.data.fields) || [];
        setFields(list);
        setValues(seedValues(list));
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          "Formularvorlage konnte nicht geladen werden.";
        setLoadError(msg);
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // initialValues bewusst nicht als Dependency: Reset erfolgt ueber key-Prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, tier]);

  const handleChange = (fieldKey, value) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
    setErrors((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  // Validierung anhand der Vorlagen-Regeln (required, minLen, maxLen, min, max).
  const validate = useCallback(() => {
    const errs = {};
    fields.forEach((f) => {
      const v = values[f.field_key];
      const rule = f.validation || {};
      const isEmpty = v === undefined || v === null || `${v}`.trim() === "";
      if (rule.required && f.field_type !== "checkbox" && isEmpty) {
        errs[f.field_key] = "Pflichtfeld.";
        return;
      }
      if (!isEmpty && typeof v === "string") {
        if (rule.minLen && v.length < rule.minLen)
          errs[f.field_key] = `Mindestens ${rule.minLen} Zeichen.`;
        if (rule.maxLen && v.length > rule.maxLen)
          errs[f.field_key] = `Höchstens ${rule.maxLen} Zeichen.`;
      }
      if (!isEmpty && f.field_type === "number") {
        const num = Number(v);
        if (Number.isNaN(num)) errs[f.field_key] = "Bitte eine Zahl eingeben.";
        else {
          if (rule.min != null && num < rule.min) errs[f.field_key] = `Mindestens ${rule.min}.`;
          if (rule.max != null && num > rule.max) errs[f.field_key] = `Höchstens ${rule.max}.`;
        }
      }
      if (!isEmpty && f.field_type === "email") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(`${v}`)) errs[f.field_key] = "Ungültige E-Mail.";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [fields, values]);

  const collect = useCallback(() => {
    const out = {};
    fields.forEach((f) => {
      out[f.field_key] = values[f.field_key];
    });
    return out;
  }, [fields, values]);

  // Erlaubt dem Aufrufer (z. B. Modal-Footer-Button), Validierung/Werte zu nutzen.
  useImperativeHandle(ref, () => ({
    submit: () => {
      if (readOnly) return false;
      if (!validate()) return false;
      if (onSubmit) onSubmit(collect());
      return true;
    },
    getValues: collect,
    validate,
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!validate()) return;
    if (onSubmit) onSubmit(collect());
  };

  const renderControl = (f) => {
    const val = values[f.field_key];
    const common = {
      id: `tf_${templateKey}_${f.field_key}`,
      disabled: readOnly || busy,
      isInvalid: !!errors[f.field_key],
    };
    if (f.field_type === "checkbox") {
      return (
        <Form.Check
          {...common}
          type="checkbox"
          label={f.label_key}
          checked={!!val}
          onChange={(e) => handleChange(f.field_key, e.target.checked)}
        />
      );
    }
    if (f.field_type === "select") {
      const opts = Array.isArray(f.allowed_values) ? f.allowed_values : [];
      return (
        <Form.Select
          {...common}
          value={val == null ? "" : val}
          onChange={(e) => handleChange(f.field_key, e.target.value)}
        >
          <option value="">Bitte wählen…</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Form.Select>
      );
    }
    if (f.field_type === "textarea") {
      return (
        <Form.Control
          {...common}
          as="textarea"
          rows={3}
          value={val == null ? "" : val}
          onChange={(e) => handleChange(f.field_key, e.target.value)}
        />
      );
    }
    const inputType =
      f.field_type === "password"
        ? "password"
        : f.field_type === "email"
        ? "email"
        : f.field_type === "number"
        ? "number"
        : f.field_type === "url"
        ? "url"
        : f.field_type === "color"
        ? "color"
        : f.field_type === "date"
        ? "date"
        : "text";
    return (
      <Form.Control
        {...common}
        type={inputType}
        value={val == null ? "" : val}
        onChange={(e) => handleChange(f.field_key, e.target.value)}
      />
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" size="sm" /> <span>Lade Formular…</span>
      </div>
    );
  }
  if (loadError) {
    return <Alert variant="danger">{loadError}</Alert>;
  }
  if (fields.length === 0) {
    return <Alert variant="info">Für diese Stufe sind keine Felder konfiguriert.</Alert>;
  }

  // Felder nach Gruppe buendeln (Reihenfolge ergibt sich aus dem Resolve).
  const groups = [];
  const groupIndex = {};
  fields.forEach((f) => {
    const g = f.group_key || "general";
    if (groupIndex[g] === undefined) {
      groupIndex[g] = groups.length;
      groups.push({ key: g, label: GROUP_LABELS[g] || g, items: [] });
    }
    groups[groupIndex[g]].items.push(f);
  });

  const colSpan = columns === 1 ? 12 : 6;

  return (
    <Form onSubmit={handleSubmit} noValidate>
      {groups.map((g) => (
        <div key={g.key} className="mb-3">
          {groups.length > 1 && (
            <h6 className="text-muted border-bottom pb-1 mb-3">{g.label}</h6>
          )}
          <Row>
            {g.items.map((f) => (
              <Col xs={12} md={f.field_type === "textarea" ? 12 : colSpan} key={f.field_key}>
                <Form.Group className="mb-3" controlId={`grp_${templateKey}_${f.field_key}`}>
                  {f.field_type !== "checkbox" && (
                    <Form.Label>
                      {f.label_key}
                      {f.validation && f.validation.required ? (
                        <span className="text-danger"> *</span>
                      ) : null}
                      {f.is_pii ? (
                        <span className="badge bg-light text-secondary ms-2" title="Personenbezogenes Datum (DSGVO)">
                          DSGVO
                        </span>
                      ) : null}
                    </Form.Label>
                  )}
                  {renderControl(f)}
                  {errors[f.field_key] && (
                    <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                      {errors[f.field_key]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      {!hideActions && (
        <div className="d-flex justify-content-end gap-2">
          {onCancel && (
            <Button variant="secondary" type="button" onClick={onCancel} disabled={busy}>
              Abbrechen
            </Button>
          )}
          {!readOnly && (
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? <Spinner animation="border" size="sm" /> : submitLabel}
            </Button>
          )}
        </div>
      )}
    </Form>
  );
});

export default TemplateForm;
